import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Flag, X, Gem, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA } from '../lib/classTheme';
import { ArenaFloor, Pedestal, Laurel, VersusEmblem } from '../components/ArenaFrame';
import { SkeletonList } from '../components/ui';

export default function RaceAccept() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const { data: car } = useQuery({ queryKey: ['car'], queryFn: () => api.get('/cars').then(r => r.data) });
  const { data: inv, isLoading, error } = useQuery({
    queryKey: ['raceInvite', code],
    queryFn: () => api.get(`/races/invite/${code}`).then(r => r.data),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.post(`/races/accept/${code}`),
    onSuccess: ({ data }) => { toast.success('Engines roaring! 🏁'); nav(`/race/${data.raceId}`, { replace: true }); },
    onError: e => toast.error(e.message),
  });

  const decline = useMutation({
    mutationFn: () => api.post(`/races/decline/${code}`),
    onSuccess: () => { toast('Declined'); nav('/race'); },
  });

  if (isLoading) return <SkeletonList n={2} h="h-56" />;
  if (error) return (
    <div className="card p-8 text-center">
      <X size={36} className="mx-auto mb-3" style={{ color: ARENA.blood }} />
      <p className="font-display font-bold">{error.message}</p>
      <Link to="/race" className="btn-primary btn-sm mt-4">Back to Circus</Link>
    </div>
  );

  const myCar = car?.car;
  const t = themeOf(car?.classKey);

  return (
    <div className="max-w-md mx-auto animate-fade-up">
      <div className="card-arena overflow-hidden">
        <ArenaFloor classKey={car?.classKey} className="p-6" showCrowd>
          <div className="text-center">
            <Laurel size={34} color={ARENA.goldL} className="mx-auto mb-1" />
            <p className="font-display text-[10px] font-black uppercase tracking-[0.25em]"
              style={{ color: ARENA.goldL }}>Race Challenge</p>
          </div>

          <div className="flex items-center justify-center gap-4 mt-5">
            <div className="text-center">
              <Pedestal glow="#c9973a" width="80%">
                <div className="w-24 h-20 rounded-lg overflow-hidden"
                  style={{ padding: 2, background: ARENA.goldFrame }}>
                  <div className="w-full h-full rounded overflow-hidden bg-stone-900">
                    {inv.car_image && <img src={inv.car_image} alt="" className="w-full h-full object-cover" />}
                  </div>
                </div>
              </Pedestal>
              <p className="font-display font-bold text-xs mt-2 text-white truncate max-w-[96px]">
                {inv.car_name}
              </p>
              <p className="text-[10px]" style={{ color: 'rgba(232,220,200,.6)' }}>
                @{inv.username} · {inv.race_elo}
              </p>
            </div>

            <VersusEmblem active color={ARENA.goldFrame} />

            <div className="text-center">
              <Pedestal glow={t.hex} width="80%">
                <div className="w-24 h-20 rounded-lg overflow-hidden"
                  style={{ padding: 2, background: ARENA.goldFrame }}>
                  <div className="w-full h-full rounded overflow-hidden bg-stone-900">
                    {myCar?.image && <img src={myCar.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                </div>
              </Pedestal>
              <p className="font-display font-bold text-xs mt-2 text-white truncate max-w-[96px]">
                {myCar?.name || '—'}
              </p>
              <p className="text-[10px]" style={{ color: 'rgba(232,220,200,.6)' }}>
                You · {myCar?.raceElo || '—'}
              </p>
            </div>
          </div>

          <div className="mt-5 text-center">
            <p className="font-display font-bold text-sm" style={{ color: ARENA.goldL }}>
              🏁 {inv.track_name}
            </p>
            <p className="text-[11px]" style={{ color: 'rgba(232,220,200,.6)' }}>
              {inv.laps} laps · {inv.difficulty}
            </p>
            {inv.stake > 0 && (
              <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl"
                style={{ background: 'rgba(232,200,116,.18)', border: '1px solid rgba(232,200,116,.35)' }}>
                <Gem size={15} style={{ color: ARENA.goldL }} />
                <span className="font-display font-black text-white">{inv.stake} points at stake</span>
              </div>
            )}
          </div>
        </ArenaFloor>

        <div className="p-4" style={{ background: 'var(--sand-100)' }}>
          {!myCar ? (
            <>
              <p className="text-sm text-center mb-3" style={{ color: 'var(--stone-600)' }}>
                You need a chariot to race.
              </p>
              <Link to="/garage" className="btn-primary w-full">Get a chariot</Link>
            </>
          ) : (
            <>
              <p className="text-sm text-center mb-3" style={{ color: 'var(--stone-600)' }}>
                <b>@{inv.username}</b> challenges you to a race.
                {inv.stake > 0 && <> Winner takes <b>{inv.stake * 2} points</b>.</>}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => decline.mutate()} className="btn-ghost">Decline</button>
                <button onClick={() => accept.mutate()} disabled={accept.isPending}
                  className="btn-primary">
                  {accept.isPending ? <><Loader2 size={15} className="animate-spin" /> Starting…</>
                    : <><Flag size={15} /> Race!</>}
                </button>
              </div>
              <p className="text-[11px] text-center mt-3" style={{ color: 'var(--stone-400)' }}>
                Costs 1 arena stamina
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}